import {
  OreText,
  OreTooltip,
} from '@edenredespana/oreneta'
import { useLayoutEffect, useRef, useState } from 'react'

export const EllipsisTextTooltip = ({ text, disabled = false }: { text?: string, disabled?: boolean }): JSX.Element => {
  const spanRef = useRef<HTMLSpanElement | null>(null)
  const [isOverflow, setIsOverflow] = useState(false)
  const value = (text ?? '').trim()

  useLayoutEffect(() => {
    const el = spanRef.current
    if (!el || typeof window === 'undefined') return

    const container = document.createElement('div')
    container.style.position = 'absolute'
    container.style.visibility = 'hidden'
    container.style.pointerEvents = 'none'
    container.style.whiteSpace = 'normal'
    container.style.wordBreak = 'break-word'
    container.style.boxSizing = 'border-box'
    document.body.appendChild(container)

    const measure = (): void => {
      const style = window.getComputedStyle(el)
      const width = el.getBoundingClientRect().width || el.clientWidth
      container.style.width = `${Math.ceil(width)}px`
      container.style.fontFamily = style.fontFamily
      container.style.fontSize = style.fontSize
      container.style.fontWeight = style.fontWeight
      container.style.lineHeight = style.lineHeight
      container.style.letterSpacing = style.letterSpacing
      container.textContent = value
      const naturalHeight = container.scrollHeight

      const lineHeightPx = Number.parseFloat(style.lineHeight)
      const twoLineHeight = Number.isFinite(lineHeightPx)
        ? Math.round(lineHeightPx * 2)
        : el.clientHeight

      setIsOverflow(naturalHeight - twoLineHeight > 0.5)
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => {
      ro.disconnect()
      document.body.removeChild(container)
    }
  }, [value])

  const content = (
    <OreText as="span" size="text-sm" tone={disabled ? 'neutral-400' : 'neutral-600'}>
      <span
        ref={spanRef}
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </OreText>
  )

  return isOverflow ? (
    <OreTooltip text={value} placement="top" separation="small">
      {content}
    </OreTooltip>
  ) : (
    content
  )
}